import { BranchCode } from "api/risk";
import { DisplayTypeEnum } from "api/files";
import { GenericColors } from "api/tenant";
import { CenteredDashboardContent } from "features/dashboard/DashboardContent";
import { CommercialIcon } from "components/icons/customIcons";
import GenericTabs from "components/navigation/Tabs/Tabs";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { toasterService } from "components/feedback/Toaster/toasterService";
import { Button } from "components/ui/Button";
import { Sizes } from "enums/sizes";
import { useDialog } from "hooks/useDialog";
import { Plus, Search } from "lucide-react";
import { FC, useCallback, useEffect, useState } from "react";
import { useSubscriptionProcess } from "../contexts/SubscriptionProcessContext";
import { ParcDto, ParcImmobilierMinimalDto } from "api/real-estate";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";
import { useAffectedQuotesConfirm } from "components/domain/AffectedQuotes";
import { AddBienToParcModal } from "./components/real-estate/AddBienToParcModal";
import { CreateParcModal } from "./components/real-estate/CreateParcModal";
import { ParcEligibilityPanel } from "./components/real-estate/ParcEligibilityPanel";
import { ParcGrid } from "./components/real-estate/ParcGrid";
import { RealEstateEligibilitySection } from "./components/real-estate/RealEstateEligibilitySection";
import { RealEstateList } from "./components/real-estate/RealEstateList";
import { useRealEstatePanel } from "./hooks/useRealEstatePanel";
import { useRealEstateRegistration } from "./hooks/useRealEstateRegistrationPage";

interface RealEstateRegistrationPageProps {
  branchCode: BranchCode;
}

type RealEstateTab = 'biens' | 'parcs';

export const RealEstateRegistrationPage: FC<RealEstateRegistrationPageProps> = ({ branchCode }) => {
  const {
    selectedRealEstateId,
    selectedParcId,
    selectedRealEstate,
    realEstates,
    realEstatesTotal,
    realEstatesPage,
    realEstatesPageSize,
    setRealEstatesPage,
    onRealEstatesSearchChange,
    isLoading,
    onSelectRealEstate,
    onSelectParc,
    onParcSelectionChange,
    clearSelection,
    stepPageConfig,
    t,
    onEligibilityChange,
    loadRealEstates,
    isSelectedRealEstateEligible,
    setPostSubmit,
    riskId,
    eligibilityRefreshKey,
    personId,
  } = useRealEstateRegistration(branchCode);

  const contextValue = useSubscriptionProcess();
  const { openDialog } = useDialog();

  const [activeTab, setActiveTab] = useState<RealEstateTab>('biens');
  const [parcs, setParcs] = useState<ParcImmobilierMinimalDto[]>([]);
  const [parcsLoading, setParcsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [parcRefreshKey, setParcRefreshKey] = useState(0);

  // Recherche serveur (débounce) de l'onglet Biens
  useEffect(() => {
    const handle = setTimeout(() => onRealEstatesSearchChange(searchTerm.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchTerm, onRealEstatesSearchChange]);

  // Le parc sélectionné est chargé en détail (biens complets) : la liste ne renvoie qu'une vue minimale.
  const [selectedParcDetail, setSelectedParcDetail] = useState<ParcDto | undefined>(undefined);

  useEffect(() => {
    if (!selectedParcId) {
      setSelectedParcDetail(undefined);
      return;
    }
    let cancelled = false;
    realEstateAPI
      .getParcById({ parcId: selectedParcId })
      .then((res) => { if (!cancelled) setSelectedParcDetail(res.realEstatePark); })
      .catch((error) => console.error("Erreur lors du chargement du parc:", error));
    return () => { cancelled = true; };
  }, [selectedParcId, parcRefreshKey]);

  const loadParcs = useCallback(async () => {
    if (!personId) return;
    setParcsLoading(true);
    try {
      // Le back liste tous les parcs du courtier : on filtre par propriétaire côté front
      const { realEstateParks } = await realEstateAPI.getListParcs();
      setParcs((realEstateParks ?? []).filter((parc) => parc.ownerId === personId));
    } catch (error) {
      console.error("Erreur lors du chargement des parcs:", error);
    } finally {
      setParcsLoading(false);
    }
  }, [personId]);

  // Après édition d'un bien : rafraîchir biens + parcs et relancer l'évaluation d'éligibilité.
  const refreshAfterBienSave = useCallback(() => {
    loadRealEstates();
    loadParcs();
    setParcRefreshKey((key) => key + 1);
  }, [loadRealEstates, loadParcs]);

  const { openCreatePanel, openEditPanel } = useRealEstatePanel({
    branchCode,
    riskId,
    contextValue,
    refreshRealEstates: refreshAfterBienSave,
    translations: {
      addTitle: t("REAL_ESTATE.LABELS.ADD_REAL_ESTATE"),
      editTitle: t("REAL_ESTATE.LABELS.EDIT_REAL_ESTATE")
    }
  });

  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(stepPageConfig);

  useEffect(() => {
    loadParcs();
  }, [loadParcs]);

  const handleTabChange = (value: number | string) => {
    setActiveTab(value as RealEstateTab);
    // Désélectionner l'objet courant lorsqu'on change d'onglet
    clearSelection();
  };

  const openCreateParcModal = useCallback(() => {
    if (!personId) {
      toasterService.showToast({
        message: "Propriétaire introuvable.",
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red
      });
      return;
    }
    openDialog(
      { title: "Créer un parc", size: Sizes['4xl'] },
      <CreateParcModal
        ownerId={personId}
        onCreated={loadParcs}
      />
    );
  }, [openDialog, personId, loadParcs]);

  const openAddBienToParcModal = useCallback(() => {
    if (!selectedParcDetail || !personId) return;
    openDialog(
      { title: "Ajouter un bien au parc", size: Sizes['4xl'] },
      <AddBienToParcModal
        parcId={selectedParcDetail.id}
        personId={personId}
        onAdded={refreshAfterBienSave}
      />
    );
  }, [openDialog, selectedParcDetail, personId, refreshAfterBienSave]);

  const deleteSelectedParc = useCallback(async () => {
    if (!selectedParcId) return;
    const response = await realEstateAPI.deleteParc({ parcId: selectedParcId });
    if (response.isSuccess) {
      toasterService.showToast({
        message: "Parc supprimé.",
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Green
      });
      clearSelection();
      loadParcs();
      loadRealEstates();
    } else {
      toasterService.showToast({
        message: response.errorMessage || "Erreur lors de la suppression du parc.",
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red
      });
    }
  }, [selectedParcId, clearSelection, loadParcs, loadRealEstates]);

  const fetchParcAffectedQuotes = useCallback(async () => {
    if (!selectedParcId) return undefined;
    return await realEstateAPI.getDevisImpactesByParc({ parcId: selectedParcId });
  }, [selectedParcId]);

  // Avertit que les simulations liées seront détachées (et à reconfigurer) avant suppression du parc
  const { showConfirmationDialog: confirmDeleteParc } = useAffectedQuotesConfirm({
    entityType: 'parc',
    fetchAffectedQuotes: fetchParcAffectedQuotes,
    onConfirm: deleteSelectedParc,
    isEditMode: true
  });

  const realEstateEmptyState = {
    title: t("REAL_ESTATE.MESSAGES.NO_REAL_ESTATE_TITLE"),
    description: t("REAL_ESTATE.MESSAGES.NO_REAL_ESTATE_DESCRIPTION"),
    actionLabel: t("REAL_ESTATE.MESSAGES.ADD_FIRST_REAL_ESTATE"),
    onAction: openCreatePanel
  };

  const parcEmptyState = {
    title: "Aucun parc",
    description: "Regroupez plusieurs biens dans un parc pour les assurer ensemble.",
    actionLabel: "Créer un parc",
    onAction: openCreateParcModal
  };

  const subtitle = "Sélectionnez un bien ou un parc à assurer";

  const addButton = (
    <Button
      color="secondary"
      round
      onClick={activeTab === 'biens' ? openCreatePanel : openCreateParcModal}
    >
      <Plus size={16} />
      {activeTab === 'biens' ? t("REAL_ESTATE.LABELS.ADD_REAL_ESTATE") : "Ajouter un parc"}
    </Button>
  );

  return (
    <CenteredDashboardContent>
      <StepPageWrapper
        stepConfig={stepConfig}
        onNext={goToNext}
        onPrevious={goToPrevious}
        withoutPadding
      >
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10">
                <CommercialIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {t("REAL_ESTATE.SECTIONS.INSURED_REAL_ASSURE")}
                </h2>
                <p className="text-sm text-slate-500">{subtitle}</p>
              </div>
            </div>
            {addButton}
          </div>

          <GenericTabs className="[&>ul]:px-5" fullWidth={false} defaultTab={activeTab} onChange={handleTabChange}>
            <GenericTabs.Item label="Biens immobiliers" value="biens" badge={realEstates.length || undefined} />
            <GenericTabs.Item label="Parc de biens immobiliers" value="parcs" badge={parcs.length || undefined} />

            <GenericTabs.Content value="biens">
              <div className="space-y-3 pb-2">
                <div className="px-4">
                  <div className="relative w-full sm:max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Rechercher un bien, une adresse…"
                      className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                </div>
                {branchCode === BranchCode.UPI && (
                  <div className="px-4">
                    <RealEstateEligibilitySection
                      branchCode={branchCode}
                      selectedRealEstateId={selectedRealEstateId}
                      onEligibilityChange={onEligibilityChange}
                      setPostSubmit={setPostSubmit}
                      refreshKey={eligibilityRefreshKey}
                    />
                  </div>
                )}
                <RealEstateList
                  realEstates={realEstates}
                  selectedRealEstateId={selectedRealEstateId}
                  isLoading={isLoading}
                  isSelectedRealEstateEligible={isSelectedRealEstateEligible}
                  onSelect={onSelectRealEstate}
                  onEdit={openEditPanel}
                  t={t}
                  currentPage={realEstatesPage}
                  totalItems={realEstatesTotal}
                  pageSize={realEstatesPageSize}
                  onPageChange={setRealEstatesPage}
                  emptyStateProps={realEstateEmptyState}
                />
                {branchCode === BranchCode.RGI && selectedRealEstate && (
                  <div className="px-4">
                    <RealEstateEligibilitySection
                      branchCode={branchCode}
                      selectedRealEstateId={selectedRealEstateId}
                      selectedRealEstate={selectedRealEstate}
                      onEligibilityChange={onEligibilityChange}
                      setPostSubmit={setPostSubmit}
                    />
                  </div>
                )}
              </div>
            </GenericTabs.Content>

            <GenericTabs.Content value="parcs">
              <div className="px-4 pb-4 space-y-4">
                <ParcGrid
                  parcs={parcs}
                  selectedParcId={selectedParcId}
                  isLoading={parcsLoading}
                  onSelect={onSelectParc}
                  emptyStateProps={parcEmptyState}
                />
                {selectedParcDetail && (
                  <ParcEligibilityPanel
                    parc={selectedParcDetail}
                    branchCode={branchCode}
                    onChange={onParcSelectionChange}
                    onEditBien={openEditPanel}
                    onAddBien={openAddBienToParcModal}
                    onDeleteParc={confirmDeleteParc}
                    refreshKey={parcRefreshKey}
                  />
                )}
              </div>
            </GenericTabs.Content>
          </GenericTabs>
        </div>
      </StepPageWrapper>
    </CenteredDashboardContent>
  );
};
